import { useCallback } from 'react';
import { cn } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import Img from '@UI/Img';
import Pin from '@Components/Pin';
import { useDeps } from '@Contexts/DI';
import styles from './ImageWithPins.module.css';

import type { FC, HTMLAttributes, MutableRefObject, MouseEvent } from 'react';
import type { FindYourOwnImage } from '@Pages/PageProduct/typings';
import type { ProductData } from '@Types/Product';
import type { PinAnalytics } from '@Components/Pin';

export interface ImageWithPinsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  img: FindYourOwnImage;
  refGrid: MutableRefObject<HTMLDivElement>;
  analyticsData: PinAnalytics;
}

const ImageWithPins: FC<ImageWithPinsProps> = (props) => {
  const { refGrid, img, analyticsData, className, ...restProps } = props;
  const { isMobile } = useMedias();
  const { analytics } = useDeps();

  const handleClickProductLink = useCallback(
    (data: { product: ProductData; position: number }) => {
      const { product, position } = data;

      if (isMobile) {
        analytics.dispatchEvent('card.click', {
          cardType: 'product',
          listTitle: analyticsData.listTitle,
          id: product.id,
          card: product,
          position,
        });
      }
    },
    [isMobile, analytics, analyticsData.listTitle],
  );

  const handleClick = useCallback(
    (e: MouseEvent, data: { product: ProductData; position: number }) => {
      const { product, position } = data;

      if (isMobile) {
        analytics.dispatchEvent('card.view', {
          id: product.id,
          position,
          listTitle: analyticsData.listTitle,
          card: product,
          cardType: 'product',
        });
      }
    },
    [isMobile, analytics, analyticsData.listTitle],
  );

  return (
    <>
      <Img
        {...restProps}
        className={cn(styles.imageWrapper, className)}
        cnImage={styles.image}
        src={img.src}
      />
      {!isMobile &&
        img.pins.map((pin, index) => {
          const updatedProduct: ProductData = {
            ...pin.product,
            category: analyticsData.listTitle,
          };
          return (
            <Pin
              key={pin.product.link}
              pinCoords={pin.coords}
              product={pin.product}
              refGrid={refGrid}
              analyticsData={analyticsData}
              onClick={(e: MouseEvent) =>
                handleClick(e, { product: updatedProduct, position: index })
              }
              onClickProductLink={() =>
                handleClickProductLink({ product: updatedProduct, position: index })
              }
            />
          );
        })}
    </>
  );
};

export default ImageWithPins;
